import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Check, HeartStraight } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import * as React from 'react';

import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';
import { getSaveSearchSubmitted } from '@/redux/modules/search/save/saveSearch.selectors';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';

type Props = {
    allSearchParams?: SearchParamsType;
    className?: string;
    deleteSavedSearch: () => void;
    isSavedSearch: boolean;
    small?: boolean;
    submitSaveSearch: () => void;
    tooltipMessage?: React.ReactNode | string;
    tooltipPlacement?: React.ComponentProps<typeof Tooltip>['place'];
};

const SaveSearch = ({
    allSearchParams,
    className,
    deleteSavedSearch,
    isSavedSearch,
    small,
    submitSaveSearch,
    tooltipMessage,
    tooltipPlacement = 'bottom',
}: Props) => {
    const { formatMessage } = useIntl();
    const submitted = useAppSelector(getSaveSearchSubmitted);
    const isMobile = useWindowIsMobile();

    const modalContext = React.useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.followSearch,
            id: 0,
            type: CompleteAccountModalContext.FollowSearch,
        }),
        []
    );

    const nonSavedSearchButton = (
        <ShouldLogInContainer
            allSearchParams={allSearchParams}
            modalContext={modalContext}
            runFunction={submitSaveSearch}
        >
            <Button
                aria-label="favorite button z-50"
                className={className}
                data-testid="saveSearchButton"
                data-tooltip-id="save-search-tooltip"
                iconLeft={HeartStraight}
                loading={submitted}
                size={small ? 'sm' : 'md'}
            >
                {formatMessage({ id: 'follow_search' })}
            </Button>
        </ShouldLogInContainer>
    );

    if (isSavedSearch) {
        return (
            <ShouldLogInContainer
                requireLogin
                runFunction={deleteSavedSearch}
            >
                <Button
                    className={className}
                    data-testid="saveSearchButton"
                    iconLeft={Check}
                    loading={submitted}
                    size={small ? 'sm' : 'md'}
                    variant="outlined"
                >
                    {formatMessage({ id: 'following_search' })}
                </Button>
            </ShouldLogInContainer>
        );
    }

    return (
        <>
            {tooltipMessage && !isMobile && (
                <Tooltip
                    className="!z-20"
                    clickable
                    id="save-search-tooltip"
                    place={tooltipPlacement}
                >
                    <span id="popover">{tooltipMessage}</span>
                </Tooltip>
            )}

            {nonSavedSearchButton}
        </>
    );
};

export default SaveSearch;
